import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Avatar,
  Divider,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  AdminPanelSettings,
  Work,
  People,
  TrendingUp,
  Visibility,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Refresh,
  Download,
  FilterList,
  Search,
} from '@mui/icons-material';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
  padding: theme.spacing(4, 0),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
  color: 'white',
  borderRadius: theme.shape.borderRadius * 2,
}));

const StatCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: theme.shape.borderRadius,
}));

interface JobPosting {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
  requirements: string[];
  salaryRange: string;
  status: 'active' | 'draft' | 'closed';
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  applications: number;
}

interface HRUser {
  id: string;
  name: string;
  email: string;
  department: string;
  role: 'hr_manager' | 'hr_specialist' | 'recruiter';
  status: 'active' | 'inactive';
  lastLogin: string;
  jobPostingsCreated: number;
}

const SuperAdmin: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Mock data - in real app, this would come from API
  const [jobPostings] = useState<JobPosting[]>([
    {
      id: '1',
      title: 'Senior Software Engineer',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time',
      description: 'Join our engineering team to build and scale our verification platform.',
      requirements: ['5+ years experience', 'React, Node.js, TypeScript', 'Cloud platforms'],
      salaryRange: '$80,000 - $120,000',
      status: 'active',
      createdAt: '2024-01-15',
      updatedAt: '2024-01-15',
      createdBy: 'Sarah Johnson',
      applications: 24
    },
    {
      id: '2',
      title: 'Product Manager',
      department: 'Product',
      location: 'Remote',
      type: 'Full-time',
      description: 'Lead product development and strategy for our verification platform.',
      requirements: ['3+ years PM experience', 'Analytical thinking', 'Agile methodologies'],
      salaryRange: '$70,000 - $100,000',
      status: 'draft',
      createdAt: '2024-01-20',
      updatedAt: '2024-01-20',
      createdBy: 'Mike Chen',
      applications: 0
    },
    {
      id: '3',
      title: 'UX/UI Designer',
      department: 'Design',
      location: 'Remote',
      type: 'Full-time',
      description: 'Create intuitive and engaging user experiences for our platform.',
      requirements: ['3+ years design experience', 'Figma expertise', 'Portfolio required'],
      salaryRange: '$60,000 - $90,000',
      status: 'closed',
      createdAt: '2024-01-10',
      updatedAt: '2024-01-25',
      createdBy: 'Sarah Johnson',
      applications: 18
    }
  ]);

  const [hrUsers] = useState<HRUser[]>([
    {
      id: '1',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@vertechie.com',
      department: 'Human Resources',
      role: 'hr_manager',
      status: 'active',
      lastLogin: '2024-01-25',
      jobPostingsCreated: 12
    },
    {
      id: '2',
      name: 'Mike Chen',
      email: 'mike.chen@vertechie.com',
      department: 'Human Resources',
      role: 'hr_specialist',
      status: 'active',
      lastLogin: '2024-01-24',
      jobPostingsCreated: 8
    },
    {
      id: '3',
      name: 'Emily Rodriguez',
      email: 'emily.rodriguez@vertechie.com',
      department: 'Human Resources',
      role: 'recruiter',
      status: 'inactive',
      lastLogin: '2024-01-20',
      jobPostingsCreated: 5
    }
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'warning';
      case 'closed': return 'error';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Active';
      case 'draft': return 'Draft';
      case 'closed': return 'Closed';
      case 'inactive': return 'Inactive';
      default: return status;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'hr_manager': return 'HR Manager';
      case 'hr_specialist': return 'HR Specialist';
      case 'recruiter': return 'Recruiter';
      default: return role;
    }
  };

  const totalJobs = jobPostings.length;
  const activeJobs = jobPostings.filter(job => job.status === 'active').length;
  const totalApplications = jobPostings.reduce((sum, job) => sum + job.applications, 0);
  const activeHRUsers = hrUsers.filter(user => user.status === 'active').length;

  const TabPanel = ({ children, value, index, ...other }: any) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <PageContainer>
      <Container maxWidth="xl">
        {/* Header */}
        <HeaderCard>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AdminPanelSettings sx={{ fontSize: 40, mr: 2 }} />
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                  Super Admin Dashboard
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Oversee HR operations and job posting management
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </HeaderCard>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Work sx={{ fontSize: 30, color: '#0077B5', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Total Jobs
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#0077B5' }}>
                {totalJobs}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeJobs} active
              </Typography>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ fontSize: 30, color: '#4caf50', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Applications
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
                {totalApplications}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total received
              </Typography>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <People sx={{ fontSize: 30, color: '#ff9800', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  HR Team
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800' }}>
                {activeHRUsers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active members
              </Typography>
            </StatCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ fontSize: 30, color: '#9c27b0', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Success Rate
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#9c27b0' }}>
                85%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Job fill rate
              </Typography>
            </StatCard>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card sx={{ mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Job Postings Overview" />
              <Tab label="HR Team Management" />
              <Tab label="Analytics & Reports" />
            </Tabs>
          </Box>

          {/* Job Postings Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                All Job Postings
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<Refresh />} variant="outlined" size="small">
                  Refresh
                </Button>
                <Button startIcon={<Download />} variant="outlined" size="small">
                  Export
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Job Title</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Applications</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobPostings.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {job.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {job.salaryRange}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={job.department} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>{job.location}</TableCell>
                      <TableCell>{job.type}</TableCell>
                      <TableCell>
                        <StatusChip 
                          label={getStatusLabel(job.status)} 
                          color={getStatusColor(job.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{job.createdBy}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ mr: 1 }}>
                            {job.applications}
                          </Typography>
                          {job.applications > 0 && (
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min((job.applications / 30) * 100, 100)} 
                              sx={{ width: 50, height: 4 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" color="primary">
                            <Visibility />
                          </IconButton>
                          <IconButton size="small" color="primary">
                            <Edit />
                          </IconButton>
                          <IconButton size="small" color="error">
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* HR Team Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                HR Team Members
              </Typography>
              <Button variant="contained" startIcon={<People />}>
                Add HR Member
              </Button>
            </Box>

            <Grid container spacing={3}>
              {hrUsers.map((user) => (
                <Grid item xs={12} md={6} lg={4} key={user.id}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: '#0077B5', mr: 2 }}>
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {user.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getRoleLabel(user.role)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {user.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Department: {user.department}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2">
                          Job Postings: {user.jobPostingsCreated}
                        </Typography>
                        <StatusChip 
                          label={getStatusLabel(user.status)} 
                          color={getStatusColor(user.status) as any}
                          size="small"
                        />
                      </Box>
                      
                      <Typography variant="caption" color="text.secondary">
                        Last Login: {user.lastLogin}
                      </Typography>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" startIcon={<Edit />}>
                          Edit
                        </Button>
                        <Button size="small" color="error" startIcon={<Delete />}>
                          Remove
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          {/* Analytics Tab */}
          <TabPanel value={activeTab} index={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Analytics & Reports
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Job Posting Trends
                    </Typography>
                    <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="text.secondary">
                        Chart visualization would go here
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Application Sources
                    </Typography>
                    <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="text.secondary">
                        Pie chart would go here
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Card>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </PageContainer>
  );
};

export default SuperAdmin;

